import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@veritrellis/ui";
import type { BuilderCondition } from "./builderTypes.js";
import type { FieldDef } from "./fieldCatalog.js";

type ConditionValueEditorProps = {
  field: FieldDef;
  condition: BuilderCondition;
  onChange: (updated: BuilderCondition) => void;
  disabled?: boolean;
};

export const ConditionValueEditor = ({
  field,
  condition,
  onChange,
  disabled = false
}: ConditionValueEditorProps) => {
  const isMultiOp = condition.op === "in" || condition.op === "not_in";
  const multiValues = Array.isArray(condition.value)
    ? condition.value.map(String)
    : [String(condition.value)];

  const toggleMultiValue = (val: string) => {
    const selected = multiValues.includes(val);
    const next = selected
      ? multiValues.filter((v) => v !== val)
      : [...new Set([...multiValues, val])];
    onChange({ ...condition, value: next });
  };

  if (field.type === "number") {
    const num = typeof condition.value === "number" ? condition.value : Number(condition.value);
    return (
      <div className="admin-condition-segment admin-condition-segment-value">
        <Input
          type="number"
          className="admin-condition-value-input"
          disabled={disabled}
          placeholder="0"
          value={Number.isNaN(num) ? "" : num}
          onChange={(e) => {
            const v = e.currentTarget.valueAsNumber;
            onChange({ ...condition, value: Number.isNaN(v) ? 0 : v });
          }}
        />
      </div>
    );
  }

  if (field.type === "enum" && isMultiOp) {
    return (
      <div className="admin-condition-segment admin-condition-segment-value admin-condition-segment-pills">
        <div className="admin-condition-pills" role="group" aria-label="Select values">
          {(field.enumValues ?? []).map((v) => {
            const active = multiValues.includes(v);
            return (
              <button
                key={v}
                type="button"
                disabled={disabled}
                className={`admin-condition-pill${active ? " admin-condition-pill-active" : ""}`}
                aria-pressed={active}
                onClick={() => toggleMultiValue(v)}
              >
                {v}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (field.type === "enum") {
    const single = Array.isArray(condition.value)
      ? (condition.value[0] ?? "")
      : String(condition.value);
    return (
      <div className="admin-condition-segment admin-condition-segment-value">
        <Select
          value={single}
          onValueChange={(v) => onChange({ ...condition, value: v })}
          disabled={disabled}
        >
          <SelectTrigger className="admin-condition-trigger admin-condition-trigger-value">
            <SelectValue placeholder="Choose…" />
          </SelectTrigger>
          <SelectContent>
            {(field.enumValues ?? []).map((v) => (
              <SelectItem key={v} value={v}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  return (
    <div className="admin-condition-segment admin-condition-segment-value">
      <Input
        className="admin-condition-value-input"
        disabled={disabled}
        placeholder="Value…"
        value={String(condition.value)}
        onChange={(e) => onChange({ ...condition, value: e.currentTarget.value })}
      />
    </div>
  );
};
